import { supabase } from "../lib/supabase";

interface RevenueData {
  daily: number;
  weekly: number;
  monthly: number;
  forecast: number;
}

export async function getAccumulatedRevenue(): Promise<RevenueData> {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 6);
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayStr = formatDate(today);
  const sevenDaysAgoStr = formatDate(sevenDaysAgo);
  const firstDayOfMonthStr = formatDate(firstDayOfMonth);

  const [dailyResult, weeklyResult, monthlyResult, forecastResult] =
    await Promise.all([
      supabase
        .from("daily_revenue")
        .select("revenue")
        .eq("revenue_date", todayStr)
        .not("worker_id", "is", null),
      supabase
        .from("daily_revenue")
        .select("revenue")
        .gte("revenue_date", sevenDaysAgoStr)
        .lte("revenue_date", todayStr)
        .not("worker_id", "is", null),
      supabase
        .from("daily_revenue")
        .select("revenue")
        .gte("revenue_date", firstDayOfMonthStr)
        .lte("revenue_date", todayStr)
        .not("worker_id", "is", null),
      // Use RPC call to fetch total forecast for the current month
      supabase.rpc("get_current_month_forecast_total"),
    ]);

  if (dailyResult.error) {
    throw new Error(
      `Error fetching daily revenue: ${dailyResult.error.message}`
    );
  }
  if (weeklyResult.error) {
    throw new Error(
      `Error fetching weekly revenue: ${weeklyResult.error.message}`
    );
  }
  if (monthlyResult.error) {
    throw new Error(
      `Error fetching monthly revenue: ${monthlyResult.error.message}`
    );
  }
  if (forecastResult.error) {
    throw new Error(
      `Error fetching forecast data: ${forecastResult.error.message}`
    );
  }

  // Sum up all workers' revenue for each period
  const daily = dailyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );
  const weekly = weeklyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );
  const monthly = monthlyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );

  // Use the pre-calculated forecast - comes as a single value from RPC
  const forecast = forecastResult.data?.[0]?.total_forecast || 0;

  return {
    daily,
    weekly,
    monthly,
    forecast,
  };
}

export interface SalesChartData {
  thisWeek: number[];
  lastWeek: number[];
}

export async function getAccumulatedSalesData(): Promise<SalesChartData> {
  // Get dates for this week and last week
  const today = new Date();
  const thisWeekDates: string[] = [];
  const lastWeekDates: string[] = [];

  // Get Monday of this week
  const monday = new Date(today);
  monday.setDate(monday.getDate() - monday.getDay() + 1);

  // Get dates for this week
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(date.getDate() + i);
    const dateStr = date.toISOString().split("T")[0];
    if (dateStr) thisWeekDates.push(dateStr);
  }

  // Get dates for last week
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(date.getDate() - 7 + i);
    const dateStr = date.toISOString().split("T")[0];
    if (dateStr) lastWeekDates.push(dateStr);
  }

  // Get revenue data for both weeks
  const { data: revenueData, error: revenueError } = await supabase
    .from("daily_revenue")
    .select("revenue_date, revenue")
    .in("revenue_date", [...thisWeekDates, ...lastWeekDates])
    .not("worker_id", "is", null);

  if (revenueError) {
    throw new Error(`Error fetching revenue data: ${revenueError.message}`);
  }

  // Group revenue by date
  const revenueLookup: Record<string, number> = {};
  (revenueData || []).forEach((entry) => {
    if (!revenueLookup[entry.revenue_date]) {
      revenueLookup[entry.revenue_date] = 0;
    }
    revenueLookup[entry.revenue_date] += Number(entry.revenue || 0);
  });

  // Fill in the data arrays
  const thisWeek = thisWeekDates.map((date) => revenueLookup[date] || 0);
  const lastWeek = lastWeekDates.map((date) => revenueLookup[date] || 0);

  return {
    thisWeek,
    lastWeek,
  };
}

export async function getIndividualRevenue(
  workerId: number
): Promise<RevenueData> {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 6);
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayStr = formatDate(today);
  const sevenDaysAgoStr = formatDate(sevenDaysAgo);
  const firstDayOfMonthStr = formatDate(firstDayOfMonth);

  const [dailyResult, weeklyResult, monthlyResult, forecastResult] =
    await Promise.all([
      supabase
        .from("daily_revenue")
        .select("revenue")
        .eq("worker_id", workerId)
        .eq("revenue_date", todayStr),
      supabase
        .from("daily_revenue")
        .select("revenue")
        .eq("worker_id", workerId)
        .gte("revenue_date", sevenDaysAgoStr)
        .lte("revenue_date", todayStr),
      supabase
        .from("daily_revenue")
        .select("revenue")
        .eq("worker_id", workerId)
        .gte("revenue_date", firstDayOfMonthStr)
        .lte("revenue_date", todayStr),
      // Get the worker's forecast using the RPC function
      supabase.rpc("get_worker_forecast", { p_worker_id: workerId }),
    ]);

  if (dailyResult.error) {
    throw new Error(
      `Error fetching daily revenue: ${dailyResult.error.message}`
    );
  }
  if (weeklyResult.error) {
    throw new Error(
      `Error fetching weekly revenue: ${weeklyResult.error.message}`
    );
  }
  if (monthlyResult.error) {
    throw new Error(
      `Error fetching monthly revenue: ${monthlyResult.error.message}`
    );
  }
  // Don't throw error for forecast - worker might not have a forecast

  const daily = Number(dailyResult.data[0]?.revenue) || 0;
  const weekly = weeklyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );
  const monthly = monthlyResult.data.reduce(
    (sum, record) => sum + Number(record.revenue || 0),
    0
  );

  // Use pre-calculated forecast if available from RPC
  let forecast = forecastResult.data?.[0]?.forecast_value || 0;

  // Fallback to simple calculation if no forecast exists
  if (!forecast) {
    const daysInMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    ).getDate();
    const daysPassed = today.getDate();
    const daysRemaining = daysInMonth - daysPassed;
    const weeklyAverage = weeklyResult.data.length
      ? weekly / weeklyResult.data.length
      : 0;
    const dailyAverage = weeklyAverage || monthly / daysPassed || 0;
    forecast = monthly + dailyAverage * daysRemaining;
  }

  return {
    daily,
    weekly,
    monthly,
    forecast,
  };
}
